import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle, FaMapMarkerAlt } from 'react-icons/fa';
import Breadcrumb from '../components/Breadcrumb.jsx';
import { setPageSEO } from '../utils/seo.js';
import { getRouteMetadata } from '../utils/seoMetadata.js';
import { getCityLocation, getCityPath } from './Locations.jsx';
import './Locations.css';

const regionProfiles = {
  Asia: {
    industries: ['technology and SaaS', 'retail and ecommerce', 'hospitality and professional services'],
    channels: ['Email follow-ups', 'WhatsApp and messaging apps', 'QR codes at physical locations', 'Post-purchase messages']
  },
  'Middle East': {
    industries: ['hospitality and tourism', 'real estate and property services', 'retail and professional services'],
    channels: ['Email and WhatsApp', 'Sales follow-ups', 'QR codes at venues', 'Post-service messages']
  },
  Europe: {
    industries: ['technology and consulting', 'retail and ecommerce', 'travel and professional services'],
    channels: ['Email campaigns', 'Customer success follow-ups', 'Website request links', 'Post-purchase messages']
  },
  'North America': {
    industries: ['technology and SaaS', 'local services and healthcare', 'retail and professional services'],
    channels: ['Email and SMS', 'Customer support follow-ups', 'QR codes', 'Sales and onboarding messages']
  },
  Oceania: {
    industries: ['professional services', 'tourism and hospitality', 'retail and technology'],
    channels: ['Email follow-ups', 'SMS and messaging apps', 'QR codes', 'Post-service requests']
  },
  Africa: {
    industries: ['technology and financial services', 'hospitality and retail', 'education and professional services'],
    channels: ['WhatsApp and messaging apps', 'Email requests', 'QR codes', 'Customer support follow-ups']
  },
  'South America': {
    industries: ['retail and ecommerce', 'tourism and hospitality', 'technology and professional services'],
    channels: ['WhatsApp and messaging apps', 'Email campaigns', 'QR codes', 'Post-purchase follow-ups']
  }
};

const indiaStateByCity = {
  Ahmedabad: 'Gujarat', Amritsar: 'Punjab', Bengaluru: 'Karnataka', Bhopal: 'Madhya Pradesh',
  Bhubaneswar: 'Odisha', Chandigarh: 'Chandigarh', Chennai: 'Tamil Nadu', Coimbatore: 'Tamil Nadu',
  Dehradun: 'Uttarakhand', Delhi: 'Delhi', Faridabad: 'Haryana', Ghaziabad: 'Uttar Pradesh',
  Goa: 'Goa', Gurugram: 'Haryana', Guwahati: 'Assam', Hyderabad: 'Telangana',
  Indore: 'Madhya Pradesh', Jaipur: 'Rajasthan', Jalandhar: 'Punjab', Jammu: 'Jammu and Kashmir',
  Jamshedpur: 'Jharkhand', Jodhpur: 'Rajasthan', Kanpur: 'Uttar Pradesh', Kochi: 'Kerala',
  Kolkata: 'West Bengal', Kozhikode: 'Kerala', Lucknow: 'Uttar Pradesh', Ludhiana: 'Punjab',
  Madurai: 'Tamil Nadu', Mangaluru: 'Karnataka', Mumbai: 'Maharashtra', Mysuru: 'Karnataka',
  Nagpur: 'Maharashtra', Nashik: 'Maharashtra', Noida: 'Uttar Pradesh', Patna: 'Bihar',
  Pune: 'Maharashtra', Raipur: 'Chhattisgarh', Rajkot: 'Gujarat', Ranchi: 'Jharkhand',
  Shimla: 'Himachal Pradesh', Surat: 'Gujarat', Thane: 'Maharashtra',
  Thiruvananthapuram: 'Kerala', Udaipur: 'Rajasthan', Vadodara: 'Gujarat',
  Varanasi: 'Uttar Pradesh', Vijayawada: 'Andhra Pradesh', Visakhapatnam: 'Andhra Pradesh'
};

const storyAngles = [
  { title: 'The starting problem', description: 'Capture what the customer needed to fix, improve, or understand before choosing your business.' },
  { title: 'The reason they chose you', description: 'Document the service, expertise, convenience, or product quality that influenced the decision.' },
  { title: 'The customer experience', description: 'Ask how communication, delivery, onboarding, and support felt throughout the relationship.' },
  { title: 'The measurable outcome', description: 'Invite customers to explain the time saved, confidence gained, or business result they experienced.' },
  { title: 'The local connection', description: 'Show how understanding the customer, market, or community contributed to a better experience.' },
  { title: 'The recommendation', description: 'Let customers explain who they would recommend the business to and why.' },
  { title: 'The moment of value', description: 'Identify the point when the customer first recognized that the product or service was working.' },
  { title: 'The long-term impact', description: 'Collect stories about continued value, repeat purchases, renewals, or an ongoing relationship.' }
];

const collectionMoments = [
  { title: 'After a successful delivery', description: 'Request feedback while the result and service experience are still fresh.' },
  { title: 'At onboarding completion', description: 'Capture first impressions after the customer has reached an early milestone.' },
  { title: 'Following a support win', description: 'Invite a story after your team has resolved an important customer issue.' },
  { title: 'When a customer renews', description: 'Ask returning customers what made them continue the relationship.' },
  { title: 'After a repeat purchase', description: 'Learn why customers came back and what they consistently value.' },
  { title: 'At a project milestone', description: 'Collect a focused response without waiting until a long engagement is complete.' },
  { title: 'After an event or visit', description: 'Use a QR code or follow-up message to reach customers soon after the experience.' },
  { title: 'When positive feedback arrives', description: 'Turn an informal compliment into an approved, reusable customer story.' }
];

function getStringValue(value) {
  return [...value].reduce((total, character) => total + character.charCodeAt(0), 0);
}

function getLocationArea(location) {
  return location.country === 'India' ? indiaStateByCity[location.city] : null;
}

function getLocalItems(items, location, count = 4) {
  const startIndex = getStringValue(`${location.city}-${location.country}`) % items.length;
  return Array.from({ length: count }, (_, index) => items[(startIndex + index) % items.length]);
}

function getLocationImage(location) {
  const imageGroup = getLocationArea(location) || location.country;
  const imageGroupSlug = imageGroup
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return `/images/locations/${imageGroupSlug}-1.webp`;
}

function CityLocation() {
  const { city: citySlug } = useParams();
  const location = getCityLocation(citySlug);

  useEffect(() => {
    if (!location) {
      return undefined;
    }

    return setPageSEO(getRouteMetadata(getCityPath(location.city)));
  }, [location]);

  if (!location) {
    return (
      <main className="locations-page">
        <section className="city-location-not-found">
          <div className="container">
            <h1>Location not found</h1>
            <p>Browse our location directory to find a supported city.</p>
            <Link to="/locations">View all locations</Link>
          </div>
        </section>
      </main>
    );
  }

  const nearbyCities = location.cities.filter((city) => city !== location.city).slice(0, 8);
  const profile = regionProfiles[location.region] || regionProfiles.Asia;
  const locationArea = getLocationArea(location);
  const locationImage = getLocationImage(location);
  const localStoryAngles = getLocalItems(storyAngles, location);
  const localCollectionMoments = getLocalItems(collectionMoments, location);
  const marketLabel = locationArea ? `${locationArea}, ${location.country}` : location.country;

  return (
    <main className="locations-page city-location-page">
      <div className="locations-breadcrumb">
        <div className="container">
          <Breadcrumb
            currentLabel={location.city}
            items={[{ label: 'Locations', to: '/locations' }]}
          />
        </div>
      </div>

      <section className="city-location-hero">
        <div className="container city-location-hero-grid">
          <div>
            <span className="locations-eyebrow">CUSTOMER PROOF IN {location.city.toUpperCase()}</span>
            <h1>Customer Testimonial Software &amp; Widgets for {location.city}</h1>
            <p>Collect, manage, and showcase customer testimonials for {location.city} with Clientalio. Use testimonial software to build trust, display reviews, and turn customer feedback into social proof.</p>
            <div className="city-location-actions">
              <Link className="city-location-primary" to="/signup">Start collecting testimonials <FaArrowRight /></Link>
              <Link className="city-location-secondary" to="/locations">View all locations</Link>
            </div>
          </div>
          <figure className="city-location-visual">
            <img
              src={locationImage}
              alt={`${location.city}, ${location.country} city view`}
              loading="eager"
            />
            <figcaption><FaMapMarkerAlt aria-hidden="true" /><span>{location.city}, {location.country}</span><small>{locationArea || location.region}</small></figcaption>
          </figure>
        </div>
      </section>

      <section className="city-location-benefits">
        <div className="container">
          <div className="city-location-intro">
            <span className="locations-eyebrow">ONE TESTIMONIAL WORKFLOW</span>
            <h2>Turn customer experiences in {location.city} into visible trust</h2>
            <p>Give customers a simple way to respond, keep approved stories organized, and add credible proof to your website.</p>
          </div>
          <div className="city-location-benefit-grid">
            <article><h3>Collect every format</h3><p>Invite customers in {location.city} to submit written reviews, record video testimonials, or share audio feedback from any device.</p></article>
            <article><h3>Manage stories centrally</h3><p>Review every submission, secure approval, and organize reusable customer proof by campaign, service, or audience.</p></article>
            <article><h3>Publish social proof</h3><p>Showcase approved {location.city} customer stories on your homepage, service pages, landing pages, and sales materials.</p></article>
          </div>
        </div>
      </section>

      <section className="city-location-detail city-location-detail-tint">
        <div className="container city-location-split">
          <div>
            <span className="locations-eyebrow">BUILT FOR {location.city.toUpperCase()} BUSINESSES</span>
            <h2>Make local customer experiences part of your marketing</h2>
            <p>Businesses across {location.city} compete for attention and trust. A specific customer story can explain your value more clearly than a generic rating because it shows the problem, experience, and outcome in a customer's own words.</p>
            <p>Clientalio gives teams in {location.country} one practical system for collecting that feedback and turning the strongest responses into credible, reusable content.</p>
          </div>
          <div className="city-location-panel">
            <h3>Useful across local industries</h3>
            <ul>{profile.industries.map((industry) => <li key={industry}><FaCheckCircle /> {industry}</li>)}</ul>
          </div>
        </div>
      </section>

      <section className="city-location-detail">
        <div className="container">
          <div className="city-location-intro">
            <span className="locations-eyebrow">A LOCAL STORY STRATEGY</span>
            <h2>What to capture from customers in {location.city}</h2>
            <p>A useful testimonial gives prospective buyers enough context to recognize their own situation. For campaigns serving {marketLabel}, build a balanced library that covers the decision, experience, result, and recommendation instead of collecting disconnected compliments.</p>
          </div>
          <div className="city-location-context-grid">
            {localStoryAngles.map((angle, index) => (
              <article key={angle.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{angle.title}</h3>
                <p>{angle.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="city-location-detail">
        <div className="container">
          <div className="city-location-intro">
            <span className="locations-eyebrow">EASY FOR EVERY CUSTOMER</span>
            <h2>Collect text, video, and audio testimonials in {location.city}</h2>
            <p>Send one branded collection link and let each customer respond in the format that feels most natural. There is no complicated setup for the customer and no need to coordinate a recording call.</p>
          </div>
          <div className="city-location-format-grid">
            <article><span>TEXT</span><h3>Written testimonials</h3><p>Ideal for product pages, proposals, service pages, and short customer quotes that buyers can scan quickly.</p></article>
            <article><span>VIDEO</span><h3>Video testimonials</h3><p>Capture the customer's voice, expression, and experience for high-trust landing pages, ads, and social campaigns.</p></article>
            <article><span>AUDIO</span><h3>Audio feedback</h3><p>Give customers a comfortable way to tell a detailed story when they prefer not to appear on camera.</p></article>
          </div>
        </div>
      </section>

      <section className="city-location-detail city-location-detail-tint">
        <div className="container">
          <div className="city-location-intro">
            <span className="locations-eyebrow">CHOOSE THE RIGHT MOMENT</span>
            <h2>Build testimonial collection into the customer journey</h2>
            <p>Teams in {location.city} can collect more useful responses by making the request part of an existing customer workflow. Start with the moments that already signal satisfaction, then assign an owner and a follow-up schedule.</p>
          </div>
          <div className="city-location-moment-grid">
            {localCollectionMoments.map((moment) => (
              <article key={moment.title}>
                <FaCheckCircle aria-hidden="true" />
                <div><h3>{moment.title}</h3><p>{moment.description}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="city-location-detail city-location-detail-dark">
        <div className="container city-location-split">
          <div>
            <span className="locations-eyebrow">ASK BETTER QUESTIONS</span>
            <h2>Help customers tell a useful, specific story</h2>
            <p>A thoughtful prompt makes it easier for customers to move beyond a star rating and explain why they chose your business.</p>
          </div>
          <ol className="city-location-question-list">
            <li><span>01</span>What problem were you trying to solve?</li>
            <li><span>02</span>Why did you choose our business?</li>
            <li><span>03</span>What changed after using our product or service?</li>
            <li><span>04</span>What would you tell another customer in {location.city}?</li>
          </ol>
        </div>
      </section>

      <section className="city-location-detail">
        <div className="container city-location-split">
          <div>
            <span className="locations-eyebrow">MEET CUSTOMERS WHERE THEY ARE</span>
            <h2>Share your testimonial request through familiar channels</h2>
            <p>Use the moments when customers are already engaged: after a purchase, a completed project, a successful support conversation, or a renewal. One link keeps the experience consistent across your team.</p>
          </div>
          <ul className="city-location-check-list">{profile.channels.map((channel) => <li key={channel}><FaCheckCircle /> {channel}</li>)}</ul>
        </div>
      </section>

      <section className="city-location-detail city-location-detail-tint">
        <div className="container">
          <div className="city-location-intro">
            <span className="locations-eyebrow">FROM RESPONSE TO RESULTS</span>
            <h2>A complete testimonial workflow for your {location.city} team</h2>
            <p>Keep customer proof under control from the first request to the final campaign. Your team always knows what has arrived, what is approved, and where each story can be used.</p>
          </div>
          <div className="city-location-process">
            <article><span>1</span><h3>Collect</h3><p>Share a simple branded request with recent customers.</p></article>
            <article><span>2</span><h3>Review</h3><p>Evaluate responses and choose the clearest stories.</p></article>
            <article><span>3</span><h3>Organize</h3><p>Keep approved proof searchable in one content library.</p></article>
            <article><span>4</span><h3>Publish</h3><p>Place testimonials where prospective buyers need confidence.</p></article>
            <article><span>5</span><h3>Reuse</h3><p>Adapt customer stories for sales, email, social, and ads.</p></article>
          </div>
        </div>
      </section>

      <section className="city-location-detail">
        <div className="container">
          <div className="city-location-intro">
            <span className="locations-eyebrow">A REPEATABLE LOCAL CAMPAIGN</span>
            <h2>Plan a testimonial campaign for {location.city}</h2>
            <p>Use a defined campaign instead of sending occasional requests. This gives your team a consistent message, a clear review process, and a growing mix of proof for different services and buyer questions.</p>
          </div>
          <div className="city-location-campaign-grid">
            <article><strong>01</strong><h3>Choose an audience</h3><p>Select recent customers with a completed experience and a clear outcome to discuss.</p></article>
            <article><strong>02</strong><h3>Define the story goal</h3><p>Decide whether the campaign needs product proof, service quality, local expertise, or customer results.</p></article>
            <article><strong>03</strong><h3>Write focused prompts</h3><p>Use three or four questions that guide the customer without scripting their response.</p></article>
            <article><strong>04</strong><h3>Send through a trusted channel</h3><p>Share the branded link using {profile.channels[0].toLowerCase()} or another familiar customer touchpoint.</p></article>
            <article><strong>05</strong><h3>Review and categorize</h3><p>Approve each response and tag it by location, service, customer type, and intended campaign.</p></article>
            <article><strong>06</strong><h3>Publish and refresh</h3><p>Place relevant stories on high-intent pages and continue collecting current proof over time.</p></article>
          </div>
        </div>
      </section>

      <section className="city-location-detail city-location-detail-dark">
        <div className="container city-location-split">
          <div>
            <span className="locations-eyebrow">KEEP CUSTOMER PROOF USEFUL</span>
            <h2>Give every testimonial context and an owner</h2>
            <p>A searchable testimonial library helps marketing, sales, and customer success use the same approved source material. Record enough context to find the right story without changing the customer's meaning.</p>
          </div>
          <ul className="city-location-library-list">
            <li><strong>Permission</strong><span>Confirm the customer has approved the content and intended use.</span></li>
            <li><strong>Context</strong><span>Keep the service, audience, location, and outcome attached to the story.</span></li>
            <li><strong>Ownership</strong><span>Assign a team member to review, publish, update, or retire each asset.</span></li>
            <li><strong>Relevance</strong><span>Match testimonials to the buyer question and page where they appear.</span></li>
          </ul>
        </div>
      </section>

      <section className="city-location-detail">
        <div className="container city-location-split">
          <div>
            <span className="locations-eyebrow">PUT PROOF TO WORK</span>
            <h2>Show customer stories wherever buyers decide</h2>
            <p>Collecting feedback is only the beginning. Publish your strongest {location.city} testimonials close to calls to action so buyers can see relevant evidence before they contact your team.</p>
          </div>
          <div className="city-location-publish-grid">
            {['Homepage', 'Product and service pages', 'Landing and pricing pages', 'Sales presentations', 'Email campaigns', 'Social media and advertising'].map((placement) => <span key={placement}>{placement}</span>)}
          </div>
        </div>
      </section>

      <section className="city-location-faq">
        <div className="container">
          <div className="city-location-intro">
            <span className="locations-eyebrow">COMMON QUESTIONS</span>
            <h2>Testimonial software in {location.city}: FAQs</h2>
          </div>
          <div className="city-location-faq-grid">
            <article><h3>Can customers respond on mobile?</h3><p>Yes. Customers can open your request link and submit text, video, or audio feedback from a compatible phone, tablet, or computer.</p></article>
            <article><h3>Can we review testimonials before publishing?</h3><p>Yes. Your team controls which submissions are approved, organized, and displayed as public customer proof.</p></article>
            <article><h3>Does Clientalio work outside {location.city}?</h3><p>Yes. Clientalio is web-based and supports distributed teams and customers across {location.country} and other international markets.</p></article>
            <article><h3>Where can we use collected testimonials?</h3><p>Use approved stories on websites, landing pages, sales materials, email campaigns, social media, and advertising.</p></article>
            <article><h3>Which businesses in {location.city} can use Clientalio?</h3><p>Clientalio supports teams in {profile.industries.join(', ')}, along with other organizations that want to collect and publish authentic customer experiences.</p></article>
            <article><h3>How often should we request new testimonials?</h3><p>Make requests after meaningful customer milestones and run focused campaigns regularly so your proof reflects current services, audiences, and outcomes.</p></article>
            <article><h3>Can testimonials be organized by location?</h3><p>Yes. Teams can organize customer stories around campaigns, services, audiences, and locations such as {location.city} to make relevant proof easier to find and reuse.</p></article>
            <article><h3>What makes a strong local testimonial?</h3><p>A strong story explains the customer's original need, why they chose the business, what the experience was like, and the result they would describe to another buyer in {marketLabel}.</p></article>
          </div>
        </div>
      </section>

      <section className="city-location-nearby">
        <div className="container">
          <div className="city-location-intro">
            <span className="locations-eyebrow">MORE IN {location.country.toUpperCase()}</span>
            <h2>Explore nearby locations</h2>
          </div>
          <div className="city-location-links">
            {nearbyCities.map((city) => <Link key={city} to={getCityPath(city)}>{city}<FaArrowRight /></Link>)}
          </div>
        </div>
      </section>

      <section className="locations-cta">
        <div className="container locations-cta-inner">
          <div><span>START IN {location.city.toUpperCase()}</span><h2>Make your customers' best stories easier to trust.</h2><p>Collect, manage, and publish authentic testimonials with Clientalio.</p></div>
          <Link to="/signup">Get started <FaArrowRight /></Link>
        </div>
      </section>
    </main>
  );
}

export default CityLocation;
